import React, { useState, useEffect } from "react";
import { Form, Row, Col, Collapse, Icon, Input } from "antd";

import useShipments from "../../../../hooks/useShipments";
import SelectInput from "../../CommonFormItems/SelectInput/SelectInput";
import SwitchInput from "../../CommonFormItems/SwitchInput/SwitchInput";
import {
  getEnabledMaterialBoxes,
  getMaterialLimit,
  getTemperatureRangeOptions,
} from "../../../../utils/materialRequestUtils";
import { getAccumulatorInstruction } from "../../../../utils/accumulatorInstructions";
import DownloadIcon from "../../../Icons/DownloadIcon";

const { Panel } = Collapse;
const { TextArea } = Input;

export default function MaterialRequestMaterialsBlock({
  form,
  materialLimit,
  onMaterialFormChange,
  showBoxesError,
}) {
  const { getSearchOptions, searchOptions } = useShipments();
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form;
  const [temperatureRange, setTemperatureRange] = useState(
    getFieldValue("temperatureRange"),
  );

  const hasMaterialLimitError = materialLimit.status === "error";
  const hasMaterialLimitLoaded = materialLimit.status === "loaded";

  // Función para obtener las opciones de temperatura del cliente
  const temperatureRangeOptions = getTemperatureRangeOptions(
    materialLimit,
    searchOptions,
  );

  const enabledMaterialBoxes = getEnabledMaterialBoxes(materialLimit);

  useEffect(() => {
    getSearchOptions();
  }, []);

  useEffect(() => {
    if (materialLimit.status !== "loaded") {
      setTemperatureRange(undefined);
    }
  }, [materialLimit.status]);

  if (hasMaterialLimitError) {
    return (
      <div className="MaterialRequestMaterialsBlock">
        <div className="material-limit-error">
          <h2>
            No ha sido posible recuperar los datos del contrato seleccionado.
          </h2>
          <h2>
            Inténtelo de nuevo más tarde o póngase en contacto con su comercial
            de la cuenta/operaciones de Correos Frio.
          </h2>
        </div>
      </div>
    );
  }

  if (!hasMaterialLimitLoaded) {
    return null;
  }

  if (enabledMaterialBoxes.length === 0) {
    return null;
  }

  return (
    <div className="MaterialRequestMaterialsBlock">
      <Row className="form-section">
        <Collapse
          defaultActiveKey={["1"]}
          expandIcon={({ isActive }) => (
            <Icon type="caret-right" rotate={isActive ? 90 : 0} />
          )}
        >
          <Panel
            header="Petición de materiales"
            key="1"
            extra={<Icon type="minus-circle" />}
            className="Busca"
          >
            <Row
              type="flex"
              gutter={[16, 16]}
              className="pr-grid-row materials-grid-row"
            >
              <Col xs={24} md={12} className="pr-col">
                <SelectInput
                  getFieldDecorator={getFieldDecorator}
                  label="Rango de temperatura"
                  name="temperatureRange"
                  options={temperatureRangeOptions}
                  callback={(value) => {
                    setTemperatureRange(value);
                    onMaterialFormChange();
                  }}
                  allowClear
                  placeholder="Pulse y seleccione uno de los rangos disponibles"
                  labelInValue
                  required
                />
              </Col>
            </Row>

            {showBoxesError && (
              <div className="material-boxes-general-error">
                Seleccione un valor para al menos un tipo de caja
              </div>
            )}

            <Row
              type="flex"
              gutter={[16, 16]}
              className="pr-grid-row material-boxes-row"
            >
              {enabledMaterialBoxes.map((box) => {
                const materialBox = materialLimit?.data?.materials?.[box.key];
                const limit = getMaterialLimit(materialBox?.limit);
                const selectedQty = Number(getFieldValue(box.key) || 0);
                const isAccumulatorDisabled = selectedQty <= 0;
                const accumulatorCode = materialBox?.accumulator?.code;
                const instruction = getAccumulatorInstruction(
                  temperatureRange,
                  box.key,
                  accumulatorCode,
                );

                return (
                  <Col xs={24} md={6} className="pr-col" key={box.key}>
                    <Form.Item
                      label={`${box.label} (máx. ${limit})`}
                      className="material-box-form-item"
                    >
                      {getFieldDecorator(box.key, {
                        rules: [
                          {
                            validator: (_, value, callback) => {
                              if (
                                value === undefined ||
                                value === null ||
                                value === ""
                              ) {
                                callback();
                                return;
                              }

                              const numericValue = Number(value);

                              if (Number.isNaN(numericValue)) {
                                callback("Debe introducir un número válido");
                                return;
                              }

                              if (numericValue < 0) {
                                callback("El valor no puede ser inferior a 0");
                                return;
                              }

                              if (numericValue > limit) {
                                callback("El valor supera el máximo permitido");
                                return;
                              }

                              callback();
                            },
                          },
                        ],
                      })(
                        <Input
                          type="number"
                          min={0}
                          max={limit}
                          onChange={(event) => {
                            const value = Number(event.target.value || 0);

                            if (value <= 0) {
                              setFieldsValue({
                                [box.accumulatorName]: false,
                              });
                            }

                            onMaterialFormChange();
                          }}
                        />,
                      )}
                    </Form.Item>

                    <div className="accumulator-switch">
                      <SwitchInput
                        getFieldDecorator={getFieldDecorator}
                        setFieldsValue={setFieldsValue}
                        label={box.accumulatorLabel}
                        name={box.accumulatorName}
                        size="small"
                        initialValue={false}
                        disabled={isAccumulatorDisabled}
                      />
                      {instruction && (
                        <a
                          className="accumulator-instructions-link"
                          href={instruction.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={instruction.file}
                        >
                          <DownloadIcon />
                          Instrucciones de preparación
                        </a>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>

            <Row
              type="flex"
              gutter={[16, 16]}
              className="pr-grid-row materials-grid-row"
            >
              <Col xs={24} md={12} className="pr-col">
                <label>Observaciones</label>
                {getFieldDecorator("observations")(
                  <TextArea rows={2} maxLength={300} />,
                )}
              </Col>
            </Row>
          </Panel>
        </Collapse>
      </Row>
    </div>
  );
}
